import { useHandlerState } from '@/store';
import { headTail } from '@/utils';
import $ from 'jquery';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './style.less';

interface ScrollbarProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    rootClassName: string,
    height: number | string,
    // [距离，时间，方向, 运动方式]
    // motionType: "continued" 持续运动
    // motionType: "beat" 跳动
    autoScroll?: [number, number, "x" | "y", "continued" | "beat"],
    hideScrollBar?: boolean
}

export default function Scrollbar(props: ScrollbarProps) {

    const { autoScroll = [], hideScrollBar = true } = props;

    let containerProps = {
        style: {
            ...props.style,
            height: props.height
        }
    }
    const content = useRef<HTMLDivElement>(null);

    // 基本状态
    const [state, setState, stateCtl] = useHandlerState({ clientWidth: 0, clientHeight: 0, scrollWidth: 0, scrollHeight: 0 });
    // 滚动条定位
    const [position, setPosition, positionCtl] = useHandlerState({ left: 0, top: 0 });
    const [cachePosition, setCachePosition, cachePositionCtl] = useHandlerState({ left: 0, top: 0 });
    // 滚动条当长度
    const [barLength, setBarLength] = useState({ row: 0, col: 0 });

    // 是否渲染（内容溢出后才可见）
    const [show, setShow, showCtl] = useHandlerState({ row: false, col: false });
    // 是否可见（鼠标移入后才可见）
    const [display, setDisplay] = useState({ row: false, col: false });
    const [moveOn, setMoveOn] = useState(false);

    // 鼠标拖动相关
    const [down, setDown, downCtl] = useHandlerState(false);
    const [downPosition, setDownPosition, downPositionCtl] = useHandlerState({ x: 0, y: 0 });
    const [move, setMove, moveCtl] = useHandlerState({ left: 0, top: 0 });

    const isAutoScroll = useCallback(() => {
        const show = showCtl.get();
        return autoScroll.length && (show.row || show.col);
    }, [autoScroll, show]);

    // 更新 content 的 scroll 坐标
    const updateContent = useCallback(() => {
        if (move.left) {
            // 起始像素 + 移动像素
            (content.current as any).scrollLeft = cachePosition.left / 100 * (state.scrollWidth - state.clientWidth) + (move.left / state.clientWidth * state.scrollWidth);
        }
        if (move.top) {
            (content.current as any).scrollTop = cachePosition.top / 100 * (state.scrollHeight - state.clientHeight) + (move.top / state.clientHeight * state.scrollHeight);
        }
    }, [move])

    // move 更新后，手动触发容器 onScroll
    useEffect(() => {
        window.requestAnimationFrame(updateContent);
    }, [move])

    // 控制滚动条显示状态
    const changeDisplayHT = useCallback(headTail(setDisplay.bind(null, { row: true, col: true }), setDisplay.bind(null, { row: false, col: false })), [])

    // 滚动事件
    function onScrollHandler(e: any) {
        const { scrollLeft, scrollTop } = e.target;

        const left = scrollLeft / (state.scrollWidth - state.clientWidth) * 100;
        const top = scrollTop / (state.scrollHeight - state.clientHeight) * 100;

        // 滚动到底部后回到顶部继续滚动
        if (!moveOn && autoScroll.length && left === 100) goStart("left");
        if (!moveOn && autoScroll.length && top === 100) goStart("top");

        // 同步滚动条位置
        setPosition(() => ({ left, top }));
        // 控制滚动条显示状态
        changeDisplayHT();
    }

    // 鼠标移动到容器上停止滚动
    const onContentMouseOverHandler = useCallback(() => {
        setMoveOn(true);
        if (autoScroll.length && (show.row || show.col)) {
            stopAutoScoll();
        }
    }, [show])

    // 鼠标移出容器上继续滚动
    const onContentMouseOutHandler = useCallback(() => {
        setMoveOn(false);
        if (autoScroll.length && (show.row || show.col)) {
            startAutoScoll();
        }
    }, [show])

    // 初始化容器的尺寸
    const init = useCallback(() => {
        setTimeout(() => {
            if (!content.current) return;
            const { clientWidth, clientHeight, scrollWidth, scrollHeight } = content.current as any;

            if (!clientWidth || !clientHeight || !scrollWidth || !scrollHeight) return;

            setState({ clientWidth, clientHeight, scrollWidth, scrollHeight });
            setBarLength({ row: clientWidth / scrollWidth * clientWidth, col: clientHeight / scrollHeight * clientHeight });
            setShow({ row: clientWidth !== scrollWidth, col: clientHeight !== scrollHeight });
        }, 1000);
    }, [])

    useLayoutEffect(() => {
        init();
        // 监听缩放
        window.onresize = init;
    }, [props.children])

    const goStart = useCallback((direction, again = true) => {
        if (direction === "left") {
            $(content.current as any).stop();
            $(content.current as any).animate({ scrollLeft: 0 }, 200, () => {
                if (again) startAutoScoll();
            });
        } else {
            $(content.current as any).stop();
            $(content.current as any).animate({ scrollTop: 0 }, 200, () => {
                if (again) startAutoScoll();
            });
        }
    }, [autoScroll]);

    // 自动滚动
    const startAutoScoll = useCallback(() => {

        const [range = 0, duration = 0, direction, motionType = "continued"] = autoScroll;
        const show = showCtl.get();

        let _direction = "";

        if (direction) _direction = direction === "x" ? "left" : "top";
        else _direction = show.row ? "left" : show.col ? "top" : "";
        if (!_direction) throw new Error("滚动方向解析错误：" + _direction);

        const { scrollWidth, scrollHeight, scrollLeft, scrollTop } = content.current as any;

        if (_direction === "left") {
            $(content.current as any).stop();
            if (motionType === "continued") {
                $(content.current as any).animate({ scrollLeft: scrollWidth }, Math.round((scrollWidth - scrollLeft) / range * duration), "linear");
            }
        } else {
            if (motionType === "continued") {
                $(content.current as any).stop();
                $(content.current as any).animate({ scrollTop: scrollHeight }, Math.round((scrollHeight - scrollTop) / range * duration), "linear");
            }
        }
    }, [show, autoScroll]);

    // 停止滚动
    const stopAutoScoll = useCallback(() => {
        $(content.current as any).stop();
    }, [])

    // 自动滚动
    useEffect(() => {
        if (autoScroll.length) {
            if (!autoScroll[0] || !autoScroll[1]) {
                throw new Error("Scrollbar autoScroll 参数错误：至少两个参数 [移动距离, 用时, 移动方向]");
            }
            if (!autoScroll[2] && show.row && show.col) throw new Error("Scrollbar autoScroll 参数错误：请指明移动方向 [移动距离, 用时, 移动方向（‘x’ | 'y'）]");
            if (show.row || show.col) {

                setCachePosition(positionCtl.get());

                setTimeout(startAutoScoll)
            }
        }
    }, [autoScroll, show])

    const onMouseOverHandler = useCallback((name) => {
        setDisplay({ ...display, [name]: true });
    }, [])

    const onMouseOutHandler = useCallback((name) => {
        if (!down) setDisplay({ ...display, [name]: false });
    }, [down])

    // 鼠标拖动移动相关 - START ------------------------------------------------------------

    const onMouseMoveHandler = useCallback((e) => {

        const event = (e.pageX ? e : (e.nativeEvent && e.nativeEvent.changedTouches[0])) || e.touches[0];

        const down = downCtl.get();

        if (!down) return;

        // 点下的位置
        const downPosition = downPositionCtl.get();
        const state = stateCtl.get();
        const move = moveCtl.get();
        // 起始坐标
        const cachePosition = cachePositionCtl.get();

        // 计算移动距离
        switch (down) {
            case "row":
                // 移动的像素
                const leftLength = event.pageX - downPosition.x;
                // 相对于容器的百分比
                const left = leftLength / state.clientWidth * 100;

                // 判断边界
                if ((cachePosition.left + left) > 100 || (cachePosition.left + left) < 0) return;
                // 保存移动的像素
                setMove({ ...move, left: leftLength });
                break;

            case "col":

                // 移动的像素
                const topLength = event.pageY - downPosition.y;
                // 相对于容器的百分比
                const top = topLength / state.clientHeight * 100;

                // 判断边界
                if ((cachePosition.top + top) > 100 || (cachePosition.top + top) < 0) return;
                // 保存移动的像素
                setMove({ ...move, top: topLength });
                break;
        }
    }, [down])

    const onMouseUpHandler = useCallback(() => {

        setMove({ left: 0, top: 0 });

        setDown(false);
        if (isAutoScroll()) startAutoScoll();

        window.removeEventListener('mousemove', onMouseMoveHandler);
        window.removeEventListener('mouseup', onMouseUpHandler);
        window.removeEventListener('touchmove', onMouseMoveHandler);
        window.removeEventListener('touchend', onMouseUpHandler);
    }, [])

    const onMouseDownHandler = useCallback((name, e) => {

        const event = e.pageX ? e : e.nativeEvent.changedTouches[0];

        setDown(name);
        setDownPosition({ x: event.pageX, y: event.pageY });

        if (isAutoScroll()) stopAutoScoll();
        setCachePosition(positionCtl.get());

        window.addEventListener('mousemove', onMouseMoveHandler);
        window.addEventListener('mouseup', onMouseUpHandler);
        window.addEventListener('touchmove', onMouseMoveHandler);
        window.addEventListener('touchend', onMouseUpHandler);
    }, [])

    // 鼠标拖动移动相关 - END ----------------------------------------------------------------

    // 滑动期间禁止滚动
    function onContainerTouchStart() {
        if (isAutoScroll()) stopAutoScoll();
    }
    // 滑动结束继续滚动
    function onContainerTouchEnd() {
        if (isAutoScroll()) startAutoScoll();
    }

    return (
        <div className={`com-scrollbar ${props.rootClassName}`}
            onMouseOver={onContentMouseOverHandler}
            onMouseOut={onContentMouseOutHandler}>
            <div {...containerProps} ref={content}
                className={`com-scrollbar__content ${props.className}`}
                onTouchStart={onContainerTouchStart}
                onTouchEnd={onContainerTouchEnd}
                onScroll={onScrollHandler}
            >
                {props.children}
            </div>
            {(show.row && hideScrollBar) && <div
                className={`com-scrollbar__rowbar ${display.row && "com-scrollbar__show"}`}
                onMouseOver={onMouseOverHandler.bind(null, "row")}
                onMouseOut={onMouseOutHandler.bind(null, "row")}
                onMouseDown={onMouseDownHandler.bind(null, "row")}
                onTouchStart={onMouseDownHandler.bind(null, "row")}
                style={{ width: `calc(100% - ${barLength.row}px)` }}><div style={{ left: `${position.left}%`, width: `${barLength.row}px` }} className="com-scrollbar__rowbar__bar"></div></div>}
            {(show.col && hideScrollBar) && <div
                className={`com-scrollbar__colbar ${display.col && "com-scrollbar__show"}`}
                onMouseOver={onMouseOverHandler.bind(null, "col")}
                onMouseOut={onMouseOutHandler.bind(null, "col")}
                onMouseDown={onMouseDownHandler.bind(null, "col")}
                onTouchStart={onMouseDownHandler.bind(null, "col")}
                style={{ height: `calc(100% - ${barLength.col}px)` }}><div style={{ top: `${position.top}%`, height: `${barLength.col}px` }} className="com-scrollbar__colbar__bar"></div></div>}
        </div>
    )
}